import PropTypes from 'prop-types';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, ButtonBase, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import CommunityCommentContainer from '../Comment/CommunityCommentContainer';

import CarouselComponent from '../../Image/CarouselComponent';
import PriceComponent from '../components/PriceComponent';

import {
  checkHasPermissions,
  formatDate,
  toSentenceCase,
} from '../../../utils';

import { COMMUNITY_POST_TYPES } from '../constants';

import permissions from '../../../permissions';
import WithLink from '../../Linkify';
import CellPersonWithImage from '../../Table/CellPersonWithImage';
import ApprovalButtons from '../components/ApprovalButtons';
import CommentsButton from '../components/CommentsButton';
import LikesButton from '../components/LikesButton';

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '1rem 0',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
    marginTop: '0',
    '&>*:first-of-type': {
      flex: 1,
    },
  },
}));

const StyledHeaderDate = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    alignSelf: 'flex-end',
    flex: 0,
  },
}));

const StyledActionButton = styled(ButtonBase)(({ theme }) => ({
  actionButton: {
    display: 'flex',
    padding: '3px',
    marginLeft: '-3px',
    borderRadius: '10px',
    whiteSpace: 'nowrap',
    '&:not(:last-child)': {
      marginRight: '3rem',
      [theme.breakpoints.down('sm')]: {
        marginRight: '1rem',
      },
    },
    '& *:first-of-type': {
      marginRight: '0.5rem',
    },
  },
}));

const CommunityPostComponent = (props) => {
  const {
    currentPost,
    onPostStatusUpdate,
    onRemovePost,
    showComments,
    onToggleComments,
    isConfirmRemovePost,
    setIsConfirmRemovePost,
  } = props;
  const {
    approvalStatus,
    community,
    createdAt,
    id,
    photos,
    price,
    sender,
    text,
    type,
    url,
    commentsCount,
    likesCount,
  } = currentPost;

  const RemoveButton = () =>
    checkHasPermissions(permissions.delete_community_post) ? (
      <StyledActionButton onClick={() => setIsConfirmRemovePost(true)}>
        <DeleteOutlineIcon />
        <Typography>Remove</Typography>
      </StyledActionButton>
    ) : null;

  const ConfirmRemoveButton = () => (
    <StyledActionButton onClick={() => onRemovePost()}>
      <CheckIcon />
      <Typography>Confirm</Typography>
    </StyledActionButton>
  );

  const CancelRemoveButton = () => (
    <StyledActionButton onClick={() => setIsConfirmRemovePost(false)}>
      <CancelIcon />
      <Typography>Cancel</Typography>
    </StyledActionButton>
  );

  const images =
    photos?.map(({ url, id }) => ({
      id,
      source: { regular: url, fullscreen: url, thumbnail: url },
    })) ?? [];

  return (
    <Box>
      <StyledHeader>
        <CellPersonWithImage
          person={sender}
          linkUrl={`/global-person-search/${sender?.id}`}
        />
        <StyledHeaderDate>{formatDate(createdAt)}</StyledHeaderDate>
      </StyledHeader>

      <Box sx={{ margin: '1rem 0' }}>
        {`${community?.name ?? 'Community'} — ${toSentenceCase(type)}`}
      </Box>

      <Typography sx={{ wordBreak: 'break-word' }}>
        {type === COMMUNITY_POST_TYPES.marketplace && (
          <PriceComponent price={price} />
        )}
        <WithLink>{text}</WithLink>
      </Typography>

      <Typography>
        <WithLink>{url}</WithLink>
      </Typography>

      <Box m="1rem 1rem 1rem 0">
        <CarouselComponent images={images} height="10rem" isSquare />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', marginLeft: '-1rem' }}>
          <CommentsButton
            onClick={() => onToggleComments()}
            commentsCount={commentsCount}
            disabled={!commentsCount}
          />
          <LikesButton
            onClick={() => {}}
            likesCount={likesCount}
            isLiked={false}
            disabled
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
          {isConfirmRemovePost ? (
            <>
              <ConfirmRemoveButton />
              <CancelRemoveButton />
            </>
          ) : (
            <RemoveButton />
          )}
        </Box>
      </Box>

      {showComments && <CommunityCommentContainer currentPostId={id} />}

      <Box my="1rem">
        {checkHasPermissions(permissions.approve_community_post) && (
          <ApprovalButtons
            approvalStatus={approvalStatus}
            onPostStatusUpdate={onPostStatusUpdate}
          />
        )}
      </Box>
    </Box>
  );
};

CommunityPostComponent.propTypes = {
  currentPost: PropTypes.instanceOf(Object).isRequired,
  showComments: PropTypes.bool.isRequired,
  isConfirmRemovePost: PropTypes.bool.isRequired,
  setIsConfirmRemovePost: PropTypes.func.isRequired,
  onPostStatusUpdate: PropTypes.func.isRequired,
  onRemovePost: PropTypes.func.isRequired,
  onToggleComments: PropTypes.func.isRequired,
};

export default CommunityPostComponent;
